"use client";

import React, { useState, useEffect, useRef } from "react";
import { Search } from "lucide-react";
import Link from "next/link";
import { SearchProduct } from "@/packages/types/products";
import { fetchSearchSuggestion } from "@/api-service/product";
import Price from "../../common/Price";
import NextImage from "../../common/NextImage";
import { useFastRouter } from "@/packages/hooks/useFastRouter";

interface SearchBarV2Props {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const placeholders = [
  "Search for traditional Indian brass utensils...",
  "Search for pure copper water bottles...",
  "Search for traditional bronze cookware...",
  "Search for authentic Indian kitchenware...",
  "Search for heritage cookware and servers..."
];

export default function SearchBarV2({ isOpen, onOpenChange }: SearchBarV2Props) {
  const [searchText, setSearchText] = useState("");
  const [suggestions, setSuggestions] = useState<SearchProduct[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Running animated placeholder text
  const [placeholder, setPlaceholder] = useState("");
  const [currentPlaceholderIdx, setCurrentPlaceholderIdx] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(100);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const fullText = placeholders[currentPlaceholderIdx];

    const handleTyping = () => {
      if (!isDeleting) {
        setPlaceholder(fullText.substring(0, placeholder.length + 1));
        setTypingSpeed(75);

        if (placeholder === fullText) {
          timer = setTimeout(() => setIsDeleting(true), 2500);
          return;
        }
      } else {
        setPlaceholder(fullText.substring(0, placeholder.length - 1));
        setTypingSpeed(35);

        if (placeholder === "") {
          setIsDeleting(false);
          setCurrentPlaceholderIdx((prev) => (prev + 1) % placeholders.length);
          setTypingSpeed(100);
          return;
        }
      }

      timer = setTimeout(handleTyping, typingSpeed);
    };

    timer = setTimeout(handleTyping, typingSpeed);
    return () => clearTimeout(timer);
  }, [placeholder, isDeleting, currentPlaceholderIdx, typingSpeed]);
  const { fastPush } = useFastRouter();

  const isControlled = isOpen !== undefined;
  const isDropdownOpen = isControlled ? isOpen : showDropdown;

  const setDropdownOpen = (open: boolean) => {
    setShowDropdown(open);
    if (onOpenChange) {
      onOpenChange(open);
    }
  };

  // Sync controlled prop changes
  useEffect(() => {
    if (isControlled) {
      setShowDropdown(isOpen);
    }
  }, [isOpen, isControlled]);

  // 1. Debounce and fetch suggestions based on 3-char minimum
  useEffect(() => {
    if (searchText.trim().length < 3) {
      setSuggestions([]);
      setTotalCount(0);
      setLoading(false);
      setDropdownOpen(false);
      return;
    }

    setLoading(true);
    const timeout = setTimeout(async () => {
      try {
        const res = await fetchSearchSuggestion(searchText);
        setSuggestions(res.data.products || []);
        setTotalCount(res.data.totalItems || 0);
        setDropdownOpen(true);
      } catch (err) {
        console.error("Search suggestions fetch failed:", err);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchText]);

  // 2. Click outside listener to auto-close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        isDropdownOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isControlled, isDropdownOpen]);

  const handleSearchSubmit = () => {
    if (searchText.trim().length === 0) return;
    setDropdownOpen(false);
    fastPush(`/search-grid/${encodeURIComponent(searchText.trim())}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchSubmit();
    }
  };


  return (
    <div className="relative flex-1 max-w-[760px] z-40" ref={dropdownRef}>
      {/* Search Input Box Wrapper - Explicit 12px rounded corners */}
      <div className="flex items-center w-full h-[48px] border border-slate-200 focus-within:border-[var(--brand-accent)] rounded-[12px] overflow-hidden bg-white focus-within:ring-4 focus-within:ring-[var(--brand-accent)]/10 transition-all duration-200">
        <input
          type="text"
          placeholder={placeholder}
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setDropdownOpen(true);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (searchText.trim().length >= 3) {
              setDropdownOpen(true);
            }
          }}
          aria-label="Search items"
          className="flex-grow min-w-0 pl-6 pr-4 h-full text-sm outline-none text-slate-800 placeholder-slate-400 font-sans"
        />
        
        {/* Taller Search Button CTA - Rounded-r 12px corners */}
        <button
          onClick={handleSearchSubmit}
          aria-label="Search"
          className="h-full w-[48px] bg-[var(--brand-accent)] hover:bg-[var(--brand-accent-hover)] text-white flex items-center justify-center transition-colors duration-200 cursor-pointer rounded-r-[12px] shrink-0"
        >
          <Search className="w-[20px] h-[20px]" />
        </button>
      </div>

      {/* Expanded Suggestions & Previews Dropdown - Rounded 12px corners */}
      {isDropdownOpen && searchText.trim().length >= 3 && (
        <div className="absolute top-[54px] left-0 w-full bg-white rounded-[12px] border border-slate-200/70 shadow-[0_20px_50px_rgba(0,0,0,0.08)] overflow-hidden flex flex-col">
          {loading ? (
            <div className="p-12 text-center text-sm text-slate-400 font-medium">
              Loading matching previews...
            </div>
          ) : suggestions.length > 0 ? (
            <>
              {/* One-Column Display Panel */}
              <div className="flex flex-col min-h-[340px]">
                
                {/* Search Results Column (100% Width) */}
                <div className="w-full p-7 flex-1 bg-white">
                  <h3 className="text-[11px] font-bold text-slate-400 tracking-wider uppercase mb-5">
                    Search Results
                  </h3>
                  
                  {/* Grid/List of 3 Product Cards - Row layout on mobile, Grid on desktop */}
                  <div className="flex flex-col sm:grid sm:grid-cols-3 gap-6 divide-y divide-slate-100 sm:divide-y-0">
                    {suggestions.slice(0, 3).map((item, idx) => {
                      return (
                        <Link
                          href={`/products/${item.slug}`}
                          key={item.id}
                          className={`group flex flex-row sm:flex-col items-center sm:items-start gap-4 sm:gap-0 cursor-pointer ${
                            idx > 0 ? "pt-4 sm:pt-0" : ""
                          }`}
                          onClick={() => {
                            setDropdownOpen(false);
                            setSearchText("");
                          }}
                        >
                          {/* Compact Image Box */}
                          <div className="w-[60px] h-[60px] sm:w-full sm:h-[200px] bg-white border border-slate-100 rounded-md flex items-center justify-center overflow-hidden p-1 sm:p-4 group-hover:border-[var(--brand-accent)] transition-all duration-200 shrink-0">
                            <NextImage
                              src={item?.images?.[0]?.thumbnailUrl?.trim() || "/assets/site/images/default.png"}
                              alt={item.title}
                              width={160}
                              height={200}
                              className="object-contain max-h-full max-w-full group-hover:scale-[1.02] transition-transform duration-300"
                            />
                          </div>

                          {/* Info Column */}
                          <div className="flex-1 min-w-0 sm:mt-4">
                            <p className="text-[13px] font-semibold text-slate-700 line-clamp-2 leading-snug sm:min-h-[38px] group-hover:text-[var(--brand-accent)] transition-colors duration-150">
                              {item.title}
                            </p>

                            {/* Pricing Block */}
                            <div className="flex flex-col mt-1 sm:mt-1.5">
                              <span className="text-sm font-bold text-slate-800">
                                <Price amount={item.salePrice} />
                              </span>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>

              </div>

              {/* Bottom Footer Action - Tighter border, bold label with high vertical padding */}
              <button
                onClick={handleSearchSubmit}
                className="w-full bg-white hover:bg-slate-50 text-center py-4 border-t border-slate-100 text-[13px] font-semibold text-slate-700 hover:text-[var(--brand-accent)] transition-all duration-200 cursor-pointer"
              >
                View all ({totalCount}) products
              </button>
            </>
          ) : (
            <div className="p-8 text-center text-xs text-slate-400 font-medium">
              No matching products found
            </div>
          )}
        </div>
      )}
    </div>
  );
}
